import { MutationAlias } from "./requestAliases/MutationAlias.js";
import { assertNever } from "../utils/assertNever.js";
class ErrorsPreprocessor {
  constructor(requestResponse, operations) {
    this.requestResponse = requestResponse;
    this.operations = operations;
  }
  preprocess() {
    const treeRoot = /* @__PURE__ */ new Map();
    if (this.requestResponse === void 0) {
      return treeRoot;
    }
    for (const operation of this.operations) {
      const mutationResponse = this.requestResponse[operation.alias];
      const processedResponse = this.processMutationResponse(mutationResponse);
      if (processedResponse === void 0) {
        continue;
      }
      const { subTreeType, subTreePlaceholder, id } = operation;
      if (subTreeType === "single") {
        if (treeRoot.has(subTreePlaceholder)) {
          return this.rejectCorruptData();
        }
        treeRoot.set(subTreePlaceholder, processedResponse);
      } else if (subTreeType === "list") {
        const child = treeRoot.get(subTreePlaceholder);
        if (child === void 0) {
          treeRoot.set(subTreePlaceholder, {
            nodeType: "iNode",
            children: /* @__PURE__ */ new Map([[id, processedResponse]]),
            validation: [],
            execution: []
          });
        } else if (child.nodeType === "iNode") {
          child.children.set(id, processedResponse);
        }
      } else {
        return assertNever();
      }
    }
    return treeRoot;
  }
  processMutationResponse(mutationResponse) {
    if (mutationResponse.ok && mutationResponse.validation?.valid && mutationResponse.errors.length === 0) {
      return void 0;
    }
    if (mutationResponse.validation?.errors.length) {
      return this.getErrorNode(mutationResponse.validation?.errors);
    }
    if (mutationResponse.errors.length) {
      return this.getErrorNode(mutationResponse.errors);
    }
    return void 0;
  }
  getErrorNode(errors) {
    const [head, ...tail] = errors;
    let rootNode = this.getRootNode(head);
    errorLoop:
      for (const error of tail) {
        let currentNode = rootNode;
        for (let i = 0, pathLength = error.path.length; i < pathLength; i++) {
          const pathNode = error.path[i];
          if (currentNode.nodeType === "leaf") {
            currentNode.nodeType = "iNode";
            currentNode.children = /* @__PURE__ */ new Map();
          }
          if (pathNode.__typename === "_FieldPathFragment") {
            if (currentNode.nodeType === "iNode") {
              let alias = pathNode.field;
              let nextIndex = i + 1;
              if (nextIndex in error.path) {
                const nextPathNode = error.path[nextIndex];
                if (nextPathNode.__typename === "_IndexPathFragment" && typeof nextPathNode.alias === "string" && nextPathNode.alias.startsWith(pathNode.field)) {
                  i++;
                  nextIndex++;
                  alias = nextPathNode.alias;
                }
              }
              if (!currentNode.children.has(alias)) {
                currentNode.children.set(alias, this.getRootNode(error, nextIndex));
                if (nextIndex <= error.path.length) {
                  continue errorLoop;
                }
              }
              currentNode = currentNode.children.get(alias);
            } else {
              this.rejectCorruptData();
            }
          } else if (pathNode.__typename === "_IndexPathFragment") {
            if (currentNode.nodeType === "iNode") {
              const alias = pathNode.alias;
              if (alias === null) {
                throw new ErrorsPreprocessor.ErrorsPreprocessorError(
                  `Corrupt data: undefined alias for node with index ${pathNode.index}.`
                );
              }
              const entityId = MutationAlias.decodeEntityId(alias);
              if (!currentNode.children.has(entityId)) {
                currentNode.children.set(entityId, this.getRootNode(error, i + 1));
                if (i + 1 <= error.path.length) {
                  continue errorLoop;
                }
              }
              currentNode = currentNode.children.get(entityId);
            } else {
              this.rejectCorruptData();
            }
          } else {
            assertNever();
          }
        }
        if (this.isExecutionError(error)) {
          currentNode.execution.push({ type: "execution", code: error.type, developerMessage: error.message });
        } else {
          currentNode.validation.push(this.createValidationError(error.message.text));
        }
      }
    return rootNode;
  }
  getRootNode(error, startIndex = 0) {
    let rootNode = {
      validation: this.isExecutionError(error) ? [] : [this.createValidationError(error.message.text)],
      execution: this.isExecutionError(error) ? [{ type: "execution", code: error.type, developerMessage: error.message }] : [],
      nodeType: "leaf"
    };
    for (let i = error.path.length - 1; i >= startIndex; i--) {
      const pathNode = error.path[i];
      if (pathNode.__typename === "_FieldPathFragment") {
        rootNode = {
          validation: [],
          execution: [],
          nodeType: "iNode",
          children: /* @__PURE__ */ new Map([[pathNode.field, rootNode]])
        };
      } else if (pathNode.__typename === "_IndexPathFragment") {
        const alias = pathNode.alias;
        if (alias === null) {
          throw new ErrorsPreprocessor.ErrorsPreprocessorError(
            `Corrupt data: undefined alias for node with index ${pathNode.index}.`
          );
        }
        rootNode = {
          validation: [],
          execution: [],
          nodeType: "iNode",
          children: /* @__PURE__ */ new Map([[MutationAlias.decodeEntityId(alias), rootNode]])
        };
      } else {
        assertNever();
      }
    }
    return rootNode;
  }
  createValidationError(message) {
    if (message === "Field is required") {
      return {
        type: "validation",
        code: "fieldRequired",
        message
      };
    }
    return { type: "validation", message, code: void 0 };
  }
  isExecutionError(error) {
    return "type" in error;
  }
  rejectCorruptData() {
    throw new ErrorsPreprocessor.ErrorsPreprocessorError(
      "Received corrupted data: a node cannot be simultaneously field-indexed and path-indexed."
    );
  }
}
((ErrorsPreprocessor2) => {
  class ErrorsPreprocessorError extends Error {
  }
  ErrorsPreprocessor2.ErrorsPreprocessorError = ErrorsPreprocessorError;
})(ErrorsPreprocessor || (ErrorsPreprocessor = {}));
export {
  ErrorsPreprocessor
};
//# sourceMappingURL=ErrorsPreprocessor.js.map
